import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Clock3, Luggage, Users, Zap } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { IMAGES, VEHICLES } from "@/lib/data";
import { useLang } from "@/lib/i18n";
import { useSeo, useHreflang } from "@/components/seo/Seo";
import { LANGS, STATIC_COPY, S, p } from "@/lib/seoContent";

const ease = [0.22, 1, 0.36, 1];
const ORDER = ["business", "first", "van", "standard"];
const INTERIORS = { business: IMAGES.sedanDark, first: IMAGES.traveler, van: IMAGES.chauffeurSuit, standard: IMAGES.fleetStandardStudio };

const FadeIn = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const GlanceCard = ({ id, index }) => {
  const { t, lang } = useLang();
  const seoPage = STATIC_COPY.fleet[lang] || STATIC_COPY.fleet.fr;
  useSeo({ lang, title: seoPage.title, description: seoPage.meta, path: p(lang, S.fleet[lang]), type: "fleet", id: "" });
  useHreflang(Object.fromEntries(LANGS.map((l) => [l, p(l, S.fleet[l])])));
  const v = VEHICLES[id];
  return (
    <FadeIn delay={index * 0.1} className="h-full">
      <a href={`#fleet-${id}`} data-testid={`fleet-glance-card-${id}`}
        className="group flex h-full flex-col rounded-[1.5rem] border border-ink/10 bg-white p-6 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-miles/35 hover:shadow-[0_24px_60px_-32px_rgba(36,87,255,0.35)]">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-miles">{String(index + 1).padStart(2, "0")}</span>
          <ArrowUpRight className="h-4 w-4 text-slate transition-[color,transform] duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-miles" strokeWidth={1.5} />
        </div>
        <div className="relative mt-2 flex h-32 items-center justify-center">
          <div className="absolute bottom-1 h-10 w-[70%] rounded-[100%] bg-miles-deep/10 blur-lg transition-transform duration-500 group-hover:scale-x-110" />
          <img src={v.image} alt={t(`veh.${id}.model`)} loading="lazy"
            className="relative h-full object-contain transition-transform duration-500 ease-out group-hover:-translate-y-1.5" />
        </div>
        <div className="mt-5 flex-1 border-t border-ink/10 pt-4">
          <h3 className="text-xl font-semibold tracking-tight text-ink">{t(`veh.${id}.name`)}</h3>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-slate">{t(`veh.${id}.model`)}</p>
          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="flex items-center gap-4 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/65">
              <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-miles" strokeWidth={1.5} />{v.pax}</span>
              <span className="flex items-center gap-1.5"><Luggage className="h-3.5 w-3.5 text-miles" strokeWidth={1.5} />{v.bags}</span>
            </span>
            <span className="text-sm font-semibold text-miles">{t("fleet.from").replace("{n}", v.from)}</span>
          </div>
        </div>
      </a>
    </FadeIn>
  );
};

const ClassSection = ({ id, index, dark = false, alt = false }) => {
  const { t } = useLang();
  const v = VEHICLES[id];
  const cls = t(`fleet.classes.${id}`);
  const veh = t(`veh.${id}`);
  const reverse = index % 2 === 1;
  const specs = [
    { icon: Users, label: t("fleet.specs.pax"), value: `${v.pax}` },
    { icon: Luggage, label: t("fleet.specs.bags"), value: `${v.bags}` },
    { icon: Clock3, label: t("fleet.specs.wait"), value: cls.wait },
    { icon: Zap, label: t("fleet.specs.hourly"), value: cls.hourly },
  ];

  return (
    <section id={`fleet-${id}`} data-testid={`fleet-detail-${id}`}
      className={`scroll-mt-24 py-20 sm:py-24 lg:py-32 ${dark ? "grain relative overflow-hidden bg-miles-deep text-white" : alt ? "bg-cloud" : "bg-white"}`}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <FadeIn className={reverse ? "lg:order-2" : ""}>
            <div className={`relative overflow-hidden rounded-[2rem] border ${dark ? "border-white/10 bg-gradient-to-b from-white/[0.08] to-transparent" : "border-ink/10 bg-gradient-to-b from-white to-miles-ice"}`}>
              <div className={`pointer-events-none absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl ${dark ? "bg-miles/25" : "bg-miles-sky/50"}`} />
              <img src={v.image} alt={veh.model} loading="lazy"
                className="img-clean relative mx-auto h-60 object-contain p-6 sm:h-80 lg:h-[26rem]" />
              <span className={`absolute left-5 top-5 rounded-full px-4 py-1.5 text-xs font-bold ${dark ? "glass-dark text-white" : "glass text-ink"}`}>
                {t("fleet.from").replace("{n}", v.from)}
              </span>
              {cls.badge && (
                <span className="absolute right-5 top-5 rounded-full bg-miles px-4 py-1.5 text-xs font-bold text-white shadow-[0_10px_28px_rgba(36,87,255,0.4)]">
                  {cls.badge}
                </span>
              )}
            </div>
            <figure className={`mt-4 overflow-hidden rounded-[1.5rem] border ${dark ? "border-white/10" : "border-ink/10"}`} data-testid={`fleet-interior-${id}`}>
              <img src={INTERIORS[id]} alt={cls.interior_alt} loading="lazy" className="img-clean h-44 w-full object-cover sm:h-52" />
              <figcaption className={`flex items-center justify-between px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] ${dark ? "bg-white/5 text-white/60" : "bg-white text-slate"}`}>
                <span>{cls.interior_caption}</span>
                <span className={dark ? "text-miles-sky" : "text-miles"}>MILES</span>
              </figcaption>
            </figure>
          </FadeIn>

          <FadeIn delay={0.12} className={reverse ? "lg:order-1" : ""}>
            <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${dark ? "text-miles-sky" : "text-miles"}`}>
              {t("fleet.cat")} {String(index + 1).padStart(2, "0")} — {veh.name}
            </p>
            <h2 className={`mt-4 text-3xl font-semibold leading-[1.05] tracking-[-0.04em] sm:text-4xl lg:text-5xl ${dark ? "text-white" : "text-ink"}`}>
              {cls.tagline}
            </h2>
            <p className={`mt-6 text-base leading-relaxed ${dark ? "text-white/70" : "text-slate"}`}>{cls.editorial}</p>

            <div className={`mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border sm:grid-cols-4 ${dark ? "border-white/10 bg-white/10" : "border-ink/10 bg-ink/10"}`}
              data-testid={`fleet-specs-${id}`}>
              {specs.map(({ icon: Icon, label, value }) => (
                <div key={label} className={`p-4 ${dark ? "bg-miles-deep" : "bg-white"}`}>
                  <Icon className="h-4 w-4 text-miles" strokeWidth={1.5} />
                  <p className={`mt-3 font-mono text-sm font-semibold ${dark ? "text-white" : "text-ink"}`}>{value}</p>
                  <p className={`mt-1 text-[11px] leading-snug ${dark ? "text-white/50" : "text-slate"}`}>{label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${dark ? "text-miles-sky" : "text-miles"}`}>{t("fleet.onboard_t")}</p>
              <ul className="mt-4 grid gap-2.5 sm:grid-cols-2" data-testid={`fleet-onboard-${id}`}>
                {cls.onboard.map((f) => (
                  <li key={f} className={`flex items-center gap-2.5 text-sm ${dark ? "text-white/85" : "text-ink/85"}`}>
                    <Check className="h-4 w-4 shrink-0 text-miles" strokeWidth={2} /> {f}
                  </li>
                ))}
              </ul>
            </div>

            <p className={`mt-7 text-xs leading-relaxed ${dark ? "text-white/50" : "text-slate"}`}>
              <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${dark ? "text-white/60" : "text-ink/60"}`}>{t("fleet.models_t")}</span>
              {" — "}{cls.models.join(" · ")}
            </p>

            <div className="mt-6 flex flex-wrap gap-2" data-testid={`fleet-ideal-${id}`}>
              {cls.ideal.map((chip) => (
                <span key={chip} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium ${dark ? "border-white/15 text-white/80" : "border-ink/10 text-ink/75"}`}>
                  {chip}
                </span>
              ))}
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Link to={`/reservation?vehicle=${id}`} data-testid={`fleet-book-${id}`}
                className="inline-flex items-center gap-2 rounded-full bg-miles px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(36,87,255,0.3)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-dark">
                {t("fleet.book")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <p className={`text-xs ${dark ? "text-white/50" : "text-slate"}`}>{t("fleet.price_note")}</p>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

const CompareTable = () => {
  const { t } = useLang();
  const cell = (id, row) => {
    const cls = t(`fleet.classes.${id}`);
    const v = VEHICLES[id];
    if (row === "models") return cls.models.join(" · ");
    if (row === "pax") return `${v.pax}`;
    if (row === "bags") return `${v.bags}`;
    if (row === "wait") return cls.wait;
    if (row === "hourly") return cls.hourly;
    if (row === "from") return `CHF ${v.from}.–`;
    return cls.ideal.join(", ");
  };
  const rows = ["models", "pax", "bags", "wait", "hourly", "from", "ideal"];

  return (
    <section className="bg-cloud py-24 lg:py-32" data-testid="fleet-compare-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <FadeIn>
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("fleet.compare_eyebrow")}</p>
          <h2 className="mt-5 text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-ink sm:text-5xl">
            {t("fleet.compare_h2a")}
            <br />
            <span className="font-light text-slate/55">{t("fleet.compare_h2b")}</span>
          </h2>
        </FadeIn>
        <FadeIn delay={0.1} className="mt-12">
          <div className="overflow-x-auto rounded-[2rem] border border-ink/10 bg-white shadow-[0_30px_80px_-52px_rgba(11,31,77,0.35)]">
            <table className="w-full min-w-[780px] border-collapse text-left" data-testid="fleet-compare-table">
              <thead>
                <tr className="border-b border-ink/10">
                  <th className="w-48 p-6" />
                  {ORDER.map((id) => (
                    <th key={id} className="p-6 align-bottom">
                      <img src={VEHICLES[id].image} alt={t(`veh.${id}.model`)} loading="lazy" className="h-16 object-contain" />
                      <p className="mt-3 text-base font-semibold tracking-tight text-ink">{t(`veh.${id}.name`)}</p>
                      <p className="mt-0.5 font-mono text-[10px] font-normal uppercase tracking-[0.16em] text-slate">{t(`veh.${id}.model`)}</p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row} className="border-b border-ink/5 last:border-0">
                    <th scope="row" className="p-6 align-top font-mono text-[10px] font-normal uppercase tracking-[0.18em] text-slate">
                      {t(`fleet.compare_rows.${row}`)}
                    </th>
                    {ORDER.map((id) => (
                      <td key={id} className={`p-6 align-top text-sm leading-relaxed ${row === "from" ? "font-mono font-semibold text-miles" : "text-ink/85"}`}>
                        {cell(id, row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </FadeIn>
        <FadeIn delay={0.15}>
          <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate" data-testid="fleet-compare-note">{t("fleet.note")}</p>
        </FadeIn>
      </div>
    </section>
  );
};

export default function Fleet() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-white text-ink" data-testid="fleet-page">
      <Navbar />

      <section className="bg-gradient-to-b from-miles-ice/60 to-white pb-16 pt-32 lg:pb-20 lg:pt-40" data-testid="fleet-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn y={16}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{t("fleet.eyebrow")}</p>
            <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] sm:text-5xl lg:text-6xl">
              {t("fleet.h1a")}
              <br />
              <span className="font-light text-slate/55">{t("fleet.h1b")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate sm:text-lg">{t("fleet.sub")}</p>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-ink/10 pt-6" data-testid="fleet-trust">
              {t("fleet.trust").map((item) => (
                <p key={item} className="flex items-center gap-2.5 text-sm font-medium text-ink/80">
                  <Check className="h-4 w-4 shrink-0 text-miles" strokeWidth={2} /> {item}
                </p>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="pb-20 pt-16 lg:pb-24 lg:pt-20" data-testid="fleet-glance">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("fleet.glance_eyebrow")}</p>
            <h2 className="mt-5 text-3xl font-semibold leading-[1.02] tracking-[-0.045em] text-ink sm:text-4xl lg:text-5xl">
              {t("fleet.glance_h2a")}{" "}
              <span className="font-light text-slate/55">{t("fleet.glance_h2b")}</span>
            </h2>
          </FadeIn>
          <div className="mt-10 grid gap-4 md:grid-cols-3 lg:gap-6">
            {ORDER.map((id, i) => <GlanceCard key={id} id={id} index={i} />)}
          </div>
        </div>
      </section>

      <ClassSection id="business" index={0} />
      <ClassSection id="first" index={1} dark />
      <ClassSection id="van" index={2} alt />
      <ClassSection id="standard" index={3} />

      <section className="py-24 lg:py-32" data-testid="fleet-included-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("fleet.included_eyebrow")}</p>
            <h2 className="mt-5 text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-ink sm:text-5xl">
              {t("fleet.included_h2a")}
              <br />
              <span className="font-light text-slate/55">{t("fleet.included_h2b")}</span>
            </h2>
          </FadeIn>
          <div className="mt-14 grid gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
            {t("fleet.included").map(([title, desc], i) => (
              <FadeIn key={title} delay={(i % 3) * 0.08}>
                <div className="border-t border-ink/10 pt-6" data-testid={`fleet-included-item-${i}`}>
                  <p className="font-mono text-[11px] font-semibold tracking-[0.2em] text-miles">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 text-lg font-semibold tracking-tight text-ink">{title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate">{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <CompareTable />

      <section className="grain relative overflow-hidden bg-miles-deep py-24 lg:py-32" data-testid="fleet-final-section">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-miles/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 left-1/4 h-80 w-80 rounded-full bg-miles-electric/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles-sky">{t("fleet.final_eyebrow")}</p>
            <h2 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-white sm:text-5xl lg:text-6xl">
              {t("fleet.final_h2a")}
              <br />
              <span className="font-light text-white/60">{t("fleet.final_h2b")}</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">{t("fleet.final_p")}</p>
            <Link to="/reservation" data-testid="fleet-final-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-miles px-8 py-4 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(36,87,255,0.4)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-dark">
              {t("fleet.final_cta")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
